/**
 * Audio device enumeration helpers.
 *
 * Mic input: enumerateDevices returns audioinput entries, which can be
 * passed to getUserMedia via `deviceId: { exact: ... }`.
 *
 * Audio output: AudioContext.setSinkId is the spec'd way to redirect
 * playback, but it ships in Chrome / Edge only as of 2026. Safari and
 * Firefox return false and we fall back silently to the system default.
 *
 * Labels are returned blank until the page has been granted mic
 * permission once; the caller is expected to call refreshDevices()
 * after a successful getUserMedia() to get the real names.
 */

export interface AudioDevice {
  readonly deviceId: string
  readonly label: string
  readonly kind: 'audioinput' | 'audiooutput'
}

export async function listAudioInputs(): Promise<ReadonlyArray<AudioDevice>> {
  return list('audioinput')
}

export async function listAudioOutputs(): Promise<ReadonlyArray<AudioDevice>> {
  return list('audiooutput')
}

async function list(kind: 'audioinput' | 'audiooutput'): Promise<ReadonlyArray<AudioDevice>> {
  if (typeof navigator === 'undefined' || typeof navigator.mediaDevices === 'undefined') {
    return []
  }
  try {
    const all = await navigator.mediaDevices.enumerateDevices()
    return all
      .filter((d) => d.kind === kind)
      .map((d) => ({
        deviceId: d.deviceId,
        label: d.label.length > 0 ? d.label : kind === 'audioinput' ? 'Microphone' : 'Speakers',
        kind,
      }))
  } catch {
    return []
  }
}

/** True if the current browser supports redirecting AudioContext output. */
export function audioContextSinkIdSupported(): boolean {
  if (typeof window === 'undefined') return false
  const proto = (window as { AudioContext?: { prototype?: { setSinkId?: unknown } } }).AudioContext?.prototype
  return typeof proto?.setSinkId === 'function'
}
